import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import api from '../api/axios';
import { applyTheme } from '../utils/theme';

const REMEMBERED_USERNAME_KEY = 'remembered_username';
const REMEMBERED_PASSWORD_KEY = 'remembered_password';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  
  const { login, isAuthenticated, isAdmin, isCashier, isStaff } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate(isAdmin() ? '/reports' : (isCashier() || isStaff()) ? '/staff-portal' : '/pos');
    }
  }, [isAuthenticated, navigate, isAdmin, isCashier, isStaff]);

  useEffect(() => {
    const rememberedUsername = localStorage.getItem(REMEMBERED_USERNAME_KEY) || '';
    const rememberedPassword = localStorage.getItem(REMEMBERED_PASSWORD_KEY) || '';
    if (rememberedUsername || rememberedPassword) {
      setUsername(rememberedUsername);
      setPassword(rememberedPassword);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    // Public branding endpoint — works without auth so the login screen shows the real logo
    api.get('/settings/branding/').then((res) => {
      setSettings(res.data);
      if (res.data?.theme_primary) applyTheme(res.data.theme_primary);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Please enter username and password');
      return;
    }

    setLoading(true);
    const result = await login(username, password);
    setLoading(false);

    if (result.success) {
      if (rememberMe) {
        localStorage.setItem(REMEMBERED_USERNAME_KEY, username);
        localStorage.setItem(REMEMBERED_PASSWORD_KEY, password);
      } else {
        localStorage.removeItem(REMEMBERED_USERNAME_KEY);
        localStorage.removeItem(REMEMBERED_PASSWORD_KEY);
      }
      toast.success('Login successful!');
      const user = useAuthStore.getState();
      navigate(user.isAdmin() ? '/reports' : (user.isCashier() || user.isStaff()) ? '/staff-portal' : '/pos');
    } else {
      toast.error(result.error);
    }
  };

  const shopLogo = settings?.shop_logo || '/logo1.png';
  const shopName = settings?.shop_name || 'POS';
  const shopTagline = settings?.shop_tagline || 'Point of Sale';

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-8 text-center">
          <img src={shopLogo} alt={shopName} className="h-16 mx-auto mb-4 object-contain" />
          <h1 className="text-3xl font-bold text-white">{shopName}</h1>
          <p className="text-primary-100 mt-2">{shopTagline}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username
            </label>
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
              placeholder="Enter your username"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition pr-12"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <label className="flex items-center gap-3 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Remember me
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <LogIn size={20} />
                Sign In
              </>
            )}
          </button>

        </form>
      </div>
    </div>
  );
};

export default Login;
